/** Every call the admin portal makes. The admin API is the backend's `/admin` module. */
import { api } from '@/api/client'
import type {
  AdminGrantChangeRequest,
  AdminGrantRequest,
  AdminMe,
  ApplicationEvent,
  ApplicationFilter,
  ApplicationRow,
  AuthSession,
  LiveItem,
  LiveStatus,
  Overview,
  PracticeInfo,
  PlansResponse,
  PracticeState,
  SubscriptionResponse,
  UserProfile,
  TokenPair,
  UserDetail,
  UserFilter,
  UserRow,
  ApplicationDetailLite,
  CommissionStatus,
  CreatePartnerRequest,
  PageInfo,
  Partner,
  PartnerCommission,
  PartnerDetail,
  PartnerReferral,
  PartnerReport,
  PartnerRow,
  PartnerSettlement,
  RecordSettlementRequest,
  UpdatePartnerRequest,
  UpdatePartnerResponse,
} from '@/types/api'

export const authApi = {
  login: (body: { email: string; password: string }) => api.postPublic<AuthSession>('/auth/login', body),
  refresh: (refreshToken: string) => api.postPublic<TokenPair>('/auth/refresh', { refreshToken }),
  logout: (refreshToken: string) => api.post<{ message: string }>('/auth/logout', { refreshToken }),
  /** 403 `admin_required` unless the account is an administrator. */
  me: () => api.get<AdminMe>('/admin/me'),
}

export const billingApi = {
  /** Public: the plans, their prices and allowances. */
  plans: () => api.get<PlansResponse>('/billing/plans'),
}

const live = (handoffId: string) => `/admin/live/${encodeURIComponent(handoffId)}`

export const adminApi = {
  overview: () => api.get<Overview>('/admin/overview'),

  users: (query: { search?: string; filter?: UserFilter; page?: number; pageSize?: number }) =>
    api.getPage<UserRow>('/admin/users', query),
  user: (userId: string) => api.get<UserDetail>(`/admin/users/${userId}`),
  userApplications: (userId: string, query: { status?: ApplicationFilter | ''; page?: number; pageSize?: number }) =>
    api.getPage<ApplicationRow>(`/admin/users/${userId}/applications`, query),
  userProfile: (userId: string) => api.get<UserProfile>(`/admin/users/${userId}/profile`),
  downloadResume: (userId: string, resumeId: string, fileName: string) => api.download(`/admin/users/${userId}/resumes/${resumeId}/download`, fileName),
  applicationEvents: (applicationId: string) => api.get<ApplicationEvent[]>(`/admin/applications/${applicationId}/events`),
  /** The application with the questions it waits on. */
  applicationDetail: (userId: string, applicationId: string) =>
    api.get<ApplicationDetailLite>(`/admin/users/${userId}/applications/${applicationId}`),
  /** Answer those questions for the candidate; `submit` sends the application too. */
  submitScreening: (userId: string, applicationId: string, body: { answers: Array<{ questionKey: string; answerValue: string | null }>; submit: boolean }) =>
    api.post<ApplicationDetailLite>(`/admin/users/${userId}/applications/${applicationId}/screening`, body),
  /** One application when `applicationId` is given, otherwise the latest `limit`. */
  retryCaptchaStopped: (userId: string, body: { applicationId?: string; limit?: number }) =>
    api.post<{ queued: string[] }>(`/admin/users/${userId}/captcha-stopped/retry`, body),

  /** Give a plan without payment; replaces an earlier admin grant. 409 while a paid (Stripe) subscription is in force. */
  grantSubscription: (userId: string, body: AdminGrantRequest) => api.post<SubscriptionResponse>(`/admin/users/${userId}/subscription`, body),
  /** Change the running admin grant's plan or end date in place. */
  changeSubscriptionGrant: (userId: string, body: AdminGrantChangeRequest) => api.patch<SubscriptionResponse>(`/admin/users/${userId}/subscription`, body),
  /** End an admin grant now. */
  endSubscriptionGrant: (userId: string) => api.delete<SubscriptionResponse>(`/admin/users/${userId}/subscription`),

  practice: () => api.get<PracticeInfo>('/admin/practice'),
  startPractice: (site: string) => api.post<PracticeState>('/admin/practice', { site }),

  live: () => api.get<LiveItem[]>('/admin/live'),
  liveStatus: (handoffId: string) => api.get<LiveStatus>(live(handoffId)),
  liveScreenshot: (handoffId: string) => api.blob(`${live(handoffId)}/screenshot`),
  liveClick: (handoffId: string, x: number, y: number) => api.post<{ message: string }>(`${live(handoffId)}/click`, { x, y }),
  liveHold: (handoffId: string, x: number, y: number, durationMs: number) =>
    api.post<{ message: string }>(`${live(handoffId)}/hold`, { x, y, durationMs }),
  liveDrag: (handoffId: string, from: { x: number; y: number }, to: { x: number; y: number }, durationMs: number) =>
    api.post<{ message: string }>(`${live(handoffId)}/drag`, { fromX: from.x, fromY: from.y, toX: to.x, toY: to.y, durationMs }),
  liveType: (handoffId: string, text: string) => api.post<{ message: string }>(`${live(handoffId)}/type`, { text }),
  liveDone: (handoffId: string) => api.post<{ message: string }>(`${live(handoffId)}/done`),
}

// ---- Partner program -------------------------------------------------------------------
const partner = (partnerId: string) => `/admin/partners/${encodeURIComponent(partnerId)}`

/** Rows of a paginated endpoint, whether it answers the usual envelope or a bare array. */
async function pageRows<T>(path: string, query?: Record<string, unknown>): Promise<{ rows: T[]; pageInfo: PageInfo | null }> {
  const payload = (await api.getPage<T>(path, query)) as unknown
  if (Array.isArray(payload)) return { rows: payload as T[], pageInfo: null }
  const body = payload as { data?: unknown; pageInfo?: PageInfo }
  if (Array.isArray(body.data)) return { rows: body.data as T[], pageInfo: body.pageInfo ?? null }
  const inner = body.data as { items?: T[]; data?: T[]; pageInfo?: PageInfo } | undefined
  return { rows: inner?.items ?? inner?.data ?? [], pageInfo: inner?.pageInfo ?? body.pageInfo ?? null }
}

const query = (params: Record<string, string | undefined>) => {
  const qs = new URLSearchParams(Object.entries(params).filter((entry): entry is [string, string] => !!entry[1])).toString()
  return qs ? `?${qs}` : ''
}

export const partnersApi = {
  list: () => api.get<PartnerRow[]>('/admin/partners'),
  /** 409 `partner_email_taken`. The tag is suggested by the server when left out. */
  create: (body: CreatePartnerRequest) => api.post<Partner>('/admin/partners', body),
  detail: (partnerId: string, days = 30) => api.get<PartnerDetail>(partner(partnerId), { days }),
  /** Lowering `maxDiscountPct` deactivates active coupons above it. */
  update: (partnerId: string, body: UpdatePartnerRequest) => api.patch<UpdatePartnerResponse>(partner(partnerId), body),
  resetPassword: (partnerId: string, password: string) => api.post<unknown>(`${partner(partnerId)}/password`, { password }),

  referrals: (partnerId: string, params: { page?: number; pageSize?: number } = {}) =>
    pageRows<PartnerReferral>(`${partner(partnerId)}/referrals`, params),
  commissions: (partnerId: string, params: { status?: CommissionStatus | ''; page?: number; pageSize?: number } = {}) =>
    pageRows<PartnerCommission>(`${partner(partnerId)}/commissions`, params),
  voidCommission: (partnerId: string, commissionId: string, reason: string) =>
    api.post<PartnerCommission>(`${partner(partnerId)}/commissions/${encodeURIComponent(commissionId)}/void`, { reason }),

  settlements: (partnerId: string) => api.get<PartnerSettlement[]>(`${partner(partnerId)}/settlements`),
  /** Settles every pending commission earned up to `upTo`; 422 `nothing_to_settle` when there are none. */
  recordSettlement: (partnerId: string, body: RecordSettlementRequest) => api.post<PartnerSettlement>(`${partner(partnerId)}/settlements`, body),

  report: (from: string, to: string) => api.get<PartnerReport>('/admin/partners/report', { from, to }),
  reportCsv: (from: string, to: string) => api.download(`/admin/partners/report${query({ from, to, format: 'csv' })}`, `partners-${from}-to-${to}.csv`),
}
